import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useLocation, useHistory } from 'react-router-dom'
import { get, post, Params } from 'api/fetch'
import { authClient } from 'auth'
import {
  PaginatedResponse,
  GpsSupplyRequest,
  TableData,
  SupplyRequestReviewStatus,
  isGpsSupplyRequest,
} from './types/types'

const apiUrl = process.env.REACT_APP_API_V2 ?? ''

const transformApiResponse = (apiData: any): GpsSupplyRequest => {
  if (!isGpsSupplyRequest(apiData)) {
    console.warn('Invalid GPS supply request data received:', apiData)
  }

  const activeTo = new Date(apiData.activeTo)
  const activeFrom = new Date(apiData.activeFrom)
  const durationInMs = activeTo.getTime() - activeFrom.getTime()
  const durationInMinutes = Math.round(durationInMs / (1000 * 60))

  return {
    id: apiData.id,
    distributionId: apiData.distributionId,
    distributionType: apiData.distributionType,
    amount: apiData.amount,
    createdAt: new Date(apiData.createdAt),
    reviewedAt: apiData.reviewedAt ? new Date(apiData.reviewedAt) : null,
    reviewedBy: apiData.reviewedBy,
    reviewStatus: apiData.reviewStatus,
    activeFrom: activeFrom,
    activeTo: activeTo,
    duration: durationInMinutes,
    centerAndRange: {
      center: {
        lat: apiData.centerAndRange.center.lat,
        long: apiData.centerAndRange.center.long,
      },
      range_meters: apiData.centerAndRange.range_meters,
    },
    drop: {
      id: apiData.drop.id,
      name: apiData.drop.name,
      description: apiData.drop.description,
      imageUrl: apiData.drop.imageUrl,
      eventUrl: apiData.drop.eventUrl,
      startDate: new Date(apiData.drop.startDate),
      endDate: new Date(apiData.drop.endDate),
      timezone: apiData.drop.timezone,
      expiryDate: new Date(apiData.drop.expiryDate),
      locationCity: apiData.drop.locationCity,
      locationCountry: apiData.drop.locationCountry,
      channel: apiData.drop.channel,
      issuerEmail: apiData.drop.issuerEmail,
      issuerId: apiData.drop.issuerId,
    },
  }
}

const getGpsPetitions = async (params: Params): Promise<TableData> => {
  const { page, limit, status, ...otherParams } = params
  const apiParams = {
    ...otherParams,
    limit,
    offset: page && limit ? ((parseInt(page as string) - 1) * parseInt(limit as string)).toString() : '0',
    ...(status && { status }),
  }

  const res = await get(`${apiUrl}/drop-requests/gps`, apiParams)

  if (res.status === 200 && res.parsedBody) {
    const { items, total, limit, offset } = res.parsedBody as PaginatedResponse<any>

    const transformedItems = items.map(transformApiResponse)

    return {
      results: transformedItems,
      meta: {
        total_results: total,
        limit,
        page: offset / limit + 1,
      },
    }
  }

  throw new Error('There was an error retrieving the data')
}

const reviewGpsPetition = async ({ reqId, decision }: { reqId: string; decision: SupplyRequestReviewStatus }) => {
  const reviewedBy = authClient.user?.email || 'admin'

  const res = await post(`${apiUrl}/drop-requests/gps/${reqId}/reviews`, {
    decision,
    reviewedBy,
  })

  if (res.status !== 200) {
    throw new Error('There was an error reviewing the petition')
  }

  return res.parsedBody
}

export const useReviewGpsPetitionMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(reviewGpsPetition, {
    onSuccess: () => {
      queryClient.invalidateQueries('getGpsPetitions')
      queryClient.invalidateQueries('recentGpsPetitions')
    },
  })
}

export const useGetGpsPetitionsQuery = (defaultParams: Params) => {
  const location = useLocation()
  const history = useHistory()
  const [queryParams, setQueryParams] = useState(defaultParams)

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const statusFromUrl = urlParams.get('status')

    if (
      statusFromUrl &&
      Object.values(SupplyRequestReviewStatus).includes(statusFromUrl as SupplyRequestReviewStatus)
    ) {
      setQueryParams((prev) => ({
        ...prev,
        status: statusFromUrl,
      }))
    }
  }, [location.search])

  const updateUrlParams = useMemo(() => {
    return (newParams: Params) => {
      const urlParams = new URLSearchParams(location.search)

      if (newParams.status) {
        urlParams.set('status', newParams.status as string)
      } else {
        urlParams.delete('status')
      }

      const newSearch = urlParams.toString()
      const currentSearch = location.search.replace('?', '')

      if (newSearch !== currentSearch) {
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname
        history.replace(newUrl)
      }
    }
  }, [location.search, location.pathname, history])

  const setQueryParamsWithUrl = useMemo(() => {
    return (newParams: Params | ((prev: Params) => Params)) => {
      const resolvedParams = typeof newParams === 'function' ? newParams(queryParams) : newParams
      setQueryParams(resolvedParams)

      if (!('page' in resolvedParams) || resolvedParams.page === queryParams.page) {
        updateUrlParams(resolvedParams)
      }
    }
  }, [queryParams, updateUrlParams])

  const petitionsQuery = useQuery(['getGpsPetitions', queryParams], () => getGpsPetitions(queryParams), {
    keepPreviousData: true,
  })

  return useMemo(
    () => ({
      setQueryParams: setQueryParamsWithUrl,
      petitionsQuery,
      currentParams: queryParams,
    }),
    [petitionsQuery, setQueryParamsWithUrl, queryParams],
  )
}
